import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Download, FileText, Sparkles, Search, Check } from "lucide-react";
import { LegalHeader, LegalFooter } from "./legal/Terms";
import { RESOURCES, findResource } from "../lib/resources";
import useSEO from "../lib/useSEO";
import { api } from "../lib/api";

// ─── Resources hub · /resources ────────────────────────────────────────────
export function ResourcesHub() {
  const [q, setQ] = useState("");
  useSEO({
    title: "Free SAP & Dynamics 365 delivery templates · Capittance Resources",
    description: "Free templates and playbooks for SAP and Dynamics 365 delivery teams. RAID logs, cutover runbooks, board packs, OCM heatmaps, value trackers.",
    path: "/resources",
  });
  const filtered = q
    ? RESOURCES.filter(r => r.title.toLowerCase().includes(q.toLowerCase())
        || r.keyword.toLowerCase().includes(q.toLowerCase()))
    : RESOURCES;
  const byTopic = filtered.reduce((acc, r) => {
    (acc[r.tocLabel] ||= []).push(r);
    return acc;
  }, {});
  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Resources" subtitle="Free SAP & Dynamics 365 delivery templates" />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="page-resources">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Free · No sign-up</div>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-zinc-900 leading-tight mt-2">The template library from 100+ SAP programmes</h1>
          <p className="text-sm md:text-base text-zinc-700 leading-relaxed max-w-3xl mt-4">
            Every template below is what we actually use on live delivery. RAID logs, cutover runbooks, board packs, fit-to-standard agendas — pragmatic, boardroom-tested, free to download.
          </p>
        </div>

        {/* Hero — S/4HANA Executive Guidebook (real, downloadable PDF) */}
        <a
          href="/downloads/guides/Capittance-S4HANA-Executive-Guidebook.pdf"
          download
          className="block mb-10 p-6 rounded-md border transition-shadow hover:shadow-lg group"
          style={{
            background: "linear-gradient(135deg, #4A0F1A 0%, #7A1F2B 60%, #B03A48 100%)",
            borderColor: "#7A1F2B",
            color: "white",
          }}
          data-testid="resources-guidebook-download"
        >
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#F0C7CE" }}>
                Free  ·  13-page PDF  ·  No email required
              </div>
              <div className="font-display text-2xl md:text-3xl font-extrabold mt-2">
                S/4HANA Executive Guidebook
              </div>
              <div className="text-sm mt-2" style={{ color: "#F3D6DB", maxWidth: "48ch" }}>
                Ten pragmatic frameworks for CIOs, CFOs and steering-committee leads. The 134-step SAP Activate checklist · 214-step cutover runbook · RAID template · Fit-to-Standard workshop · Data migration · Testing · Integration · OCM · Hypercare · Value realisation.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-2 px-5 py-3 rounded-sm font-mono text-[11px] uppercase tracking-[0.2em] font-bold group-hover:translate-x-1 transition-transform"
                    style={{ background: "white", color: "#7A1F2B" }}>
                Download PDF  <ArrowRight size={14} strokeWidth={2.4} />
              </span>
            </div>
          </div>
        </a>

        <div className="mb-6 relative max-w-lg">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search templates — e.g. RAID, cutover, board pack…"
            className="w-full border border-[var(--border)] rounded-sm py-2.5 pl-9 pr-3 text-sm outline-none focus:border-cyan-500"
            data-testid="resources-search"
          />
        </div>

        {Object.entries(byTopic).map(([topic, items]) => (
          <div key={topic} className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700 mb-2">{topic}</div>
            <div className="grid md:grid-cols-2 gap-3">
              {items.map(r => (
                <Link key={r.slug} to={`/resources/${r.slug}`} className="card p-4 hover:shadow-md transition-shadow group" data-testid={`resource-card-${r.slug}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-sm bg-cyan-50 grid place-items-center text-cyan-700 shrink-0"><FileText size={16}/></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-base font-bold tracking-tight text-zinc-900 leading-snug">{r.title.replace(" · Free Download","")}</div>
                      <div className="text-xs text-zinc-600 mt-1 line-clamp-2 leading-relaxed">{r.metaDesc}</div>
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-cyan-700 group-hover:underline inline-flex items-center gap-1">
                        Read + download <ArrowRight size={11}/>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-6 text-center text-sm text-zinc-500 italic">No templates match &ldquo;{q}&rdquo;. Try RAID, cutover, board, migration, OCM, testing.</div>}

        <LegalFooter />
      </main>
    </div>
  );
}

// ─── Resource detail · /resources/:slug ────────────────────────────────────
export function ResourceDetail() {
  const { slug } = useParams();
  const r = findResource(slug);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  // Guard the hook: only call when we have a real resource
  const seoTitle = r ? `${r.title} · Capittance` : "";
  const seoDesc = r ? r.metaDesc : "";
  const seoPath = r ? `/resources/${r.slug}` : "";
  useSEO({ title: seoTitle, description: seoDesc, path: seoPath, type: "article" });

  if (!r) return <Navigate to="/resources" replace />;

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    // Repurpose the /api/support/contact endpoint as the lead-capture sink
    try {
      await api.contact({
        name: "Resource download",
        email: email.trim().toLowerCase(),
        subject: `Resource download · ${r.slug}`,
        message: `Automated: ${email} requested the ${r.keyword} template.`,
      });
      setSent(true);
    } catch {
      // still show the "sent" state so the user isn't blocked from the content
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title={r.tocLabel} subtitle={r.keyword} />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-resource-detail">
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Free download · SAP/D365 delivery</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 leading-tight mt-2" data-testid="resource-hero">{r.hero}</h1>
          <p className="text-base text-zinc-700 leading-relaxed mt-3">{r.sub}</p>
        </div>

        {/* Inline lead-capture card */}
        <div className="card p-6 mb-10" style={{ background: "linear-gradient(135deg,#F0F9FF 0%,#E0F2FE 100%)" }} data-testid="resource-lead-capture">
          {!sent ? (
            <form onSubmit={submitEmail} className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex-1">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-sky-800 mb-1">Email to receive the template</label>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com"
                       className="w-full border border-sky-200 rounded-sm py-2.5 px-3 text-sm outline-none focus:border-sky-500 bg-white" data-testid="resource-email-input"/>
              </div>
              <button type="submit" disabled={busy} className="btn btn-primary inline-flex items-center gap-1.5 sm:mt-[22px]" data-testid="resource-email-submit">
                <Download size={13}/> Send me the template
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="resource-lead-success">
              <Check size={16}/> Sent. Check your inbox. Meanwhile, keep reading — the full template is described below and mirrored in the Capittance app.
            </div>
          )}
          <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-sky-800/70">No spam · No newsletter · You can delete us anytime</div>
        </div>

        {r.sections.map((s, i) => (
          <div key={i} className="mb-8" data-testid={`resource-section-${i}`}>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-zinc-900 mb-2">{s.h}</h2>
            <p className="text-base text-zinc-700 leading-relaxed">{s.p}</p>
          </div>
        ))}

        {/* Footer CTA */}
        <div className="card p-8 text-center relative overflow-hidden mt-10" style={{ background: "linear-gradient(135deg,#FEF7ED 0%,#FEF3C7 100%)" }}>
          <Sparkles className="mx-auto text-amber-600 mb-2" size={22}/>
          <h3 className="font-display text-xl md:text-2xl font-extrabold tracking-tight">Run this template live inside Capittance</h3>
          <p className="text-sm text-zinc-700 mt-2 max-w-xl mx-auto">
            The Capittance Harmonizer platform ships with every template on this page as a live, autosaving module — no downloads, no version drift, exports to PDF/Word/PPT with one click.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/pricing" className="btn btn-secondary btn-sm" data-testid="resource-cta-pricing">See pricing</Link>
            <Link to="/signup" className="btn btn-primary btn-sm inline-flex items-center gap-1.5" data-testid="resource-cta-signup">
              Start now <ArrowRight size={13}/>
            </Link>
          </div>
        </div>

        {/* Related */}
        <div className="mt-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Related templates</div>
          <div className="grid md:grid-cols-2 gap-3">
            {RESOURCES.filter(x => x.slug !== r.slug).slice(0, 4).map(x => (
              <Link key={x.slug} to={`/resources/${x.slug}`} className="card p-3 hover:shadow-sm text-sm" data-testid={`resource-related-${x.slug}`}>
                <div className="font-display font-bold text-zinc-900">{x.title.replace(" · Free Download", "")}</div>
                <div className="text-xs text-zinc-600 mt-0.5 line-clamp-1">{x.metaDesc}</div>
              </Link>
            ))}
          </div>
        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
